import React from 'react'
import {
  BulkDeleteButton,
  CheckboxGroupInput,
  CreateBase,
  Datagrid,
  EditBase,
  // FormDataConsumer,
  FunctionField,
  type Identifier,
  type InputProps,
  // List,
  ListBase,
  ListToolbar,
  Loading,
  Pagination,
  type RaRecord,
  required,
  type RowClickFunction,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
  useUnselectAll
} from 'react-admin'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  compact,
  difference,
  find,
  groupBy,
  includes,
  isEmpty,
  isNil,
  join,
  keys,
  map,
  reduce,
  size,
  slice,
  startsWith,
  uniq
} from 'lodash/fp'
import log from 'loglevel'
import { type ConfigProps } from 'components/Dashboard'
import { SolarAccountsUploadDialog } from 'components/Upload'
import { SolarAccountsDownloadDialog } from 'components/Download'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
import { assign } from 'lodash'
import { useGetMappings } from 'components/DataProvider'
import { type Mapping } from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const MANAGEDBY_MAPPINGS = ['manager', 'user', 'owner', 'partner']

export const Accounts: React.FC<
  ConfigProps & { partner: Partial<Partner> }
> = ({ config, partner }) => {
  const { Accounts: configAccounts, Mappings: configMappings } = config
  const notify = useNotify()
  const { organizations } = configMappings
  const translate = useTranslate()
  const [mutate, setMutate] = React.useState(false)
  const [mode, setMode] = React.useState<string | undefined>(undefined)
  const [record, setRecord] = React.useState<string | undefined>(undefined)
  const [upload, setUpload] = React.useState(false)
  const [download, setDownload] = React.useState(false)
  const [notReady, setNotReday] = React.useState(false)
  const refresh = useRefresh()
  const unselectAll = useUnselectAll('accounts')
  const handleCreate = (): void => {
    setMode('create')
    setMutate(true)
  }
  const handleUpload = (): void => {
    setUpload(true)
  }
  const handleDownload = (): void => {
    setDownload(true)
  }
  const AccountsListActions: React.FC = () => {
    return (
      <TopToolbar>
        <Button
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          variant="contained">
          <Typography variant="h6">
            {translate('resources.accounts.buttons.download')}
          </Typography>
        </Button>
        <Button
          onClick={handleUpload}
          startIcon={<UploadIcon />}
          variant="contained">
          <Typography variant="h6">
            {translate('resources.accounts.buttons.upload')}
          </Typography>
        </Button>
        <Button
          onClick={handleCreate}
          startIcon={<AddIcon />}
          variant="contained">
          <Typography variant="h6">
            {translate('resources.accounts.buttons.create')}
          </Typography>
        </Button>
      </TopToolbar>
    )
  }
  const AccountsMutateFormContent: React.FC<{
    edit?: boolean | null | undefined
  }> = ({ edit }) => {
    const { setValue, getValues } = useFormContext()
    const [disabled, setDisabled] = React.useState(false)
    const { group } = configAccounts
    const values = getValues()
    const groupValue = useWatch({ name: 'group' })
    const group2 = useWatch({ name: 'group2' })
    const organizationValues = map((key: string) => values[key])(organizations)
    React.useEffect(() => {
      const notReady =
        !isNil(
          find(
            (item: string) =>
              startsWith('manager#')(item) && item !== 'manager#0'
          )(groupValue)
        ) &&
        (isEmpty(organizationValues[0]) || isNil(organizationValues[0]))
      setNotReday(notReady)
    }, [groupValue, organizationValues])
    const GroupInput: React.FC<InputProps> = () => {
      React.useEffect(() => {
        if (includes('admin')(groupValue) && !includes('admin')(group2)) {
          setValue('group2', ['admin'], {
            shouldDirty: true
          })
        }
        if (includes('demo')(groupValue) && !includes('demo')(group2)) {
          setValue('group2', ['demo'], {
            shouldDirty: true
          })
        }
        // const group1 = filter((item: string) => item !== 'admin')(groupValue)
        // setValue('group1', isEmpty(group1) ? undefined : group1[0], {
        //   shouldDirty: true
        // })
      }, [groupValue, group2])
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 2,
            width: '100%'
          }}>
          <SelectInput
            source="group1"
            label="resources.accounts.fields.group"
            choices={compact(
              map((key: string) =>
                key === 'admin' || key === 'demo'
                  ? undefined
                  : { id: key, name: group[key] }
              )(
                includes('demo')(group2)
                  ? ['user']
                  : !includes('admin')(group2) || !isNil(process.env['TEST'])
                  ? keys(group)
                  : ['highManager']
              )
            )}
            sx={{ width: '50%' }}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement> | RaRecord<Identifier>
            ): void => {
              const { group2 } = getValues()
              const group1 = isEmpty(event.target.value)
                ? null
                : event.target.value
              setDisabled(includes(group1)(MANAGEDBY_MAPPINGS))
              setValue(
                'group',
                compact(isNil(group2) ? [group1] : [group1, ...group2]),
                {
                  shouldDirty: true,
                  shouldValidate: true
                }
              )
            }}
          />
          <CheckboxGroupInput
            source="group2"
            label={false}
            choices={
              includes('demo')(keys(group))
                ? [
                    {
                      id: 'admin',
                      name: group.admin,
                      disabled:
                        includes('demo')(group2) ||
                        !includes('highManager')(groupValue)
                    },
                    {
                      id: 'demo',
                      name: group.demo,
                      disabled:
                        includes('admin')(group2) ||
                        !includes('user')(groupValue)
                    }
                  ]
                : [
                    {
                      id: 'admin',
                      name: group.admin,
                      disabled: !includes('highManager')(groupValue)
                    }
                  ]
            }
            onChange={(...event: any[]): void => {
              const { group1 } = getValues()
              const group2 = isEmpty(event[0]) ? [] : event[0]
              setValue(
                'group',
                compact(
                  isNil(group1) || isEmpty(group1)
                    ? group2
                    : [group1, ...group2]
                ),
                {
                  shouldDirty: true,
                  shouldValidate: true
                }
              )
            }}
          />
        </Box>
      )
    }
    return (
      <>
        <TextInput source="name" fullWidth validate={required()} />
        <TextInput
          source="email"
          type="email"
          readOnly={!isNil(edit) && edit}
          fullWidth
          validate={required()}
        />
        <GroupInput source="group" />
        <OrganizationsInput
          config={config}
          partner={partner.id as string}
          disabled={disabled}
          setDisabled={setDisabled}
        />
      </>
    )
  }
  const handleClose = (): void => {
    setMutate(false)
  }
  const AccountsMutateToolbar: React.FC<{ notReady: boolean }> = ({
    notReady
  }) => {
    return (
      <Toolbar>
        <Button variant="outlined" onClick={handleClose} sx={{ mr: '1em' }}>
          {translate('resources.accounts.dialogs.mutate.buttons.cancel')}
        </Button>
        <SaveButton
          label="resources.accounts.dialogs.mutate.buttons.done"
          disabled={notReady}
        />
      </Toolbar>
    )
  }
  const dialog = React.useMemo(() => {
    return (
      <Dialog onClose={handleClose} open={mutate} maxWidth="md" fullWidth>
        <DialogTitle>
          {!isNil(mode)
            ? translate(`resources.accounts.dialogs.${mode}.title`)
            : ''}
        </DialogTitle>
        <DialogContent>
          {mode === 'create' && (
            <CreateBase
              resource="accounts"
              redirect={false}
              mutationOptions={{
                onSuccess: (data) => {
                  handleClose()
                  refresh()
                },
                onError: () => {
                  notify('account create failed')
                  handleClose()
                  refresh()
                }
              }}>
              <SimpleForm
                toolbar={<AccountsMutateToolbar notReady={notReady} />}>
                <AccountsMutateFormContent />
              </SimpleForm>
            </CreateBase>
          )}
          {mode === 'edit' && !isNil(record) && (
            <EditBase
              id={record}
              resource="accounts"
              redirect={false}
              mutationMode="pessimistic"
              mutationOptions={{
                onSuccess: () => {
                  handleClose()
                  refresh()
                }
              }}>
              <SimpleForm
                toolbar={<AccountsMutateToolbar notReady={notReady} />}>
                <AccountsMutateFormContent edit />
              </SimpleForm>
            </EditBase>
          )}
        </DialogContent>
      </Dialog>
    )
  }, [mode, mutate, notReady])
  const AccountsListContent: React.FC = () => {
    const { isLoading, selectedIds } = useListContext()
    if (!isNil(isLoading) && isLoading) return <Loading />
    const handleRowClick: RowClickFunction = (
      id: Identifier,
      _resource: string,
      _record: RaRecord
    ): any => {
      setMode('edit')
      setMutate(true)
      setRecord(id as string)
      return false
    }
    const { group, useArea, useOffice } = configAccounts
    return (
      <Box
        sx={{
          width: '100%',
          overflowX: !isEmpty(selectedIds) ? 'none' : 'scroll'
        }}>
        <Datagrid
          rowClick={handleRowClick}
          bulkActionButtons={
            <>
              <BulkDeleteButton
                mutationMode="pessimistic"
                confirmTitle="resources.accounts.dialogs.confirm.title"
                confirmContent="resources.accounts.dialogs.confirm.content"
              />
            </>
          }>
          <TextField source="name" sx={{ display: 'block', width: 150 }} />
          <TextField source="email" sx={{ display: 'block', width: 300 }} />
          <FunctionField
            label="resources.accounts.fields.group"
            render={(record: { group: string[] }) =>
              join(',')(map((item: string) => group[item])(record.group))
            }
            sx={{ display: 'block', width: 200 }}
          />
          {!isNil(useArea) && (useArea as boolean) && (
            <FunctionField
              label={organizations[0]}
              render={(record: { organizations: string[] }) =>
                record.organizations[0]
              }
              sx={{ display: 'block', width: 100 }}
            />
          )}
          {!isNil(useOffice) && (useOffice as boolean) && (
            <FunctionField
              label={organizations[1]}
              render={(record: { organizations: string[] }) =>
                record.organizations[1]
              }
              sx={{ display: 'block', width: 100 }}
            />
          )}
          <FunctionField
            label="resources.accounts.fields.status"
            render={(record: { status: string }) =>
              record.status === 'CONFIRMED' ? '設定済み' : '未設定'
            }
            sx={{ display: 'block', width: 100 }}
          />
        </Datagrid>
      </Box>
    )
  }
  const accountListFilters = [
    <TextInput source="name" alwaysOn resettable />,
    <TextInput source="email" alwaysOn resettable />,
    <SelectInput
      source="group"
      label="resources.accounts.fields.group"
      choices={map((key: string) => ({
        id: key,
        name: configAccounts.group[key]
      }))(keys(configAccounts.group))}
      alwaysOn
      resettable
    />
  ]
  React.useLayoutEffect(() => {
    unselectAll()
  }, [])
  return (
    <>
      <ListBase resource="accounts" disableSyncWithLocation perPage={50}>
        <ListToolbar
          actions={<AccountsListActions />}
          filters={accountListFilters}
        />
        <AccountsListContent />
        <Pagination />
      </ListBase>
      {mutate && !isNil(mode) && dialog}
      <SolarAccountsUploadDialog
        open={upload}
        setOpen={setUpload}
        partner={partner}
      />
      <SolarAccountsDownloadDialog
        open={download}
        setOpen={setDownload}
        partner={partner}
        config={config}
      />
    </>
  )
}
export const OrganizationsInput: React.FC<
  ConfigProps & {
    partner: string
    disabled?: boolean
    setDisabled?: (disabled: boolean) => void
  }
> = ({ config, partner, disabled, setDisabled }) => {
  const { organizations, organizationOrders } = config.Mappings
  const [allOrganizations, setAllOrganizations] = React.useState<
    Record<string, Array<string | Mapping>>
  >({})
  // const [allArea, setAllArea] = React.useState<string[]>([])
  // const [allOffice, setAllOffice] = React.useState<any>([])
  const { setValue, getValues } = useFormContext()
  // const [selectArea, setArea] = React.useState<string | undefined>(
  //   getValues()[organizations[0]]
  // )
  const [selected, setSelected] = React.useState<
    Record<string, string | undefined>
  >(
    reduce(
      (sum: Record<string, string | undefined>, key: string) => ({
        ...sum,
        [key]: getValues()[key]
      }),
      {}
    )(organizations)
  )
  // const [disabled, setDisabled] = React.useState(false)
  const { data, isLoading } = useGetMappings(partner)
  const values = getValues()
  const { name, group } = values
  React.useEffect(() => {
    if (
      isNil(group) ||
      includes(find((item: string) => item !== 'admin')(group) ?? '')(
        MANAGEDBY_MAPPINGS
      )
    ) {
      const managed = find((item: any) => item.managedBy === name)(data)
      if (!isNil(managed)) {
        for (const key of organizations) {
          // setValue('area', managed[area], {
          //   shouldDirty: true
          // })
          // setArea(managed[area])
          // setValue('office', managed[office], {
          //   shouldDirty: true
          // })
          setValue(key, managed[key], {
            shouldDirty: true
          })
        }
        map((key: string) => {
          assign(selected, {
            [key]: managed[key]
          })
        })(slice(0, size(organizations) - 1)(organizations))
        setSelected(selected)
        // setArea(managed[organizations[0]])
      }
      if (!isNil(setDisabled)) setDisabled(true)
    } else {
      if (!isNil(setDisabled)) setDisabled(false)
    }
  }, [setDisabled, name, group, data])
  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (isLoading || isNil(data)) return
    if (!isEmpty(allOrganizations)) return
    // if (!isEmpty(allArea) && !isEmpty(allOffice)) return
    // const { area, office } = configMappings
    // const items = reduce(
    //   (
    //     sum: {
    //       [area]: string[]
    //       [office]: Array<{ [area]: string; [office]: string }>
    //     },
    //     item: { [area]: string; [office]: string }
    //   ) => {
    //     const areaSum = sum[area] as string[]
    //     areaSum.push(item[area] as string)
    //     const officeSum = sum[office] as Array<{
    //       [area]: string
    //       [office]: string
    //     }>
    //     officeSum.push(item)
    //     return { [area]: uniq(areaSum), [office]: uniq(officeSum) }
    //   },
    //   { [area]: [], [office]: [] }
    // )(data)
    // setAllArea(items[area] as string[])
    const items = reduce(
      (sum: Record<string, Array<string | Mapping>>, item: Mapping) => {
        // @ts-expect-error ts(2339)
        const items = map.convert({ cap: false })(
          (key: string, index: number) => {
            if (index === 0) {
              const itemSum = sum[key] as string[]
              itemSum.push(item[key] as string)
              return { [key]: uniq(itemSum) }
            } else {
              const itemSum = sum[key] as Mapping[]
              itemSum.push(item)
              return { [key]: uniq(itemSum) }
            }
          }
        )(organizations)
        return reduce(
          (
            sum: Record<string, Array<string | Mapping>>,
            item: Record<string, Array<string | Mapping>>
          ) => ({ ...sum, ...item }),
          {}
        )(items)
      },
      reduce(
        (sum: Record<string, Array<string | Mapping>>, key: string) => ({
          ...sum,
          [key]: []
        }),
        {}
      )(organizations)
    )(data)
    // @ts-expect-error ts(2339)
    map.convert({ cap: false })((organization: string, index: number) => {
      const values = groupBy<Record<string, string>>(organizations[index])(
        items[organization] as string[]
      )
      assign(items, {
        [organization]: reduce(
          (sum: Record<string, string[]>, key: string) => ({
            ...sum,
            [key]: uniq(
              map(
                (item: Record<string, string>) => item[organization] as string
              )(values[key])
            )
          }),
          {}
        )(keys(values))
      })
    })(slice(1, size(organizations))(organizations))
    setAllOrganizations(items)
    // const offices = groupBy<{ [area]: string; [office]: string }>(area)(
    //   items[office] as string[]
    // )
    // setAllOffice(
    //   reduce(
    //     (sum: Record<string, any>, key: string) => ({
    //       ...sum,
    //       [key]: uniq(map((item: any) => item[office])(offices[key]))
    //     }),
    //     {}
    //   )(keys(offices))
    // )
  }, [
    isLoading,
    data,
    setAllOrganizations,
    allOrganizations,
    // setAllArea,
    // setAllOffice,
    // allArea,
    // allOffice,
    values,
    setDisabled
  ])
  const setOrganizations = (
    order: string[] | null | undefined,
    organizations: string[]
  ): string[] => {
    if (isNil(order)) return organizations
    const diff = difference(organizations)(order)
    if (!isEmpty(diff)) {
      return [...order, ...diff]
    } else {
      return order
    }
  }
  // @ts-expect-error ts(2339)
  return map.convert({ cap: false })((key: string, index: number) => {
    const organizationOrder = !isNil(organizationOrders)
      ? organizationOrders[index]
      : undefined
    const choices =
      index === 0
        ? !isNil(disabled) && (disabled as boolean)
          ? // ? [{ id: selectArea, name: selectArea }]
            [
              {
                id: selected[organizations[0]],
                name: selected[organizations[0]]
              }
            ]
          : map((item: string) => ({
              id: item,
              name: item
              // }))(allArea)
            }))(
              setOrganizations(
                organizationOrder,
                allOrganizations[organizations[0]] as string[]
              )
            )
        : isNil(selected[organizations[index - 1]])
        ? []
        : map((item: string) => ({
            id: item,
            name: item
            // }))(allOffice[selectArea as keyof typeof allOffice])
          }))(
            setOrganizations(
              organizationOrder,
              (allOrganizations[organizations[index]] ?? {
                [selected[organizations[index - 1]] as string]: []
              })[selected[organizations[index - 1]] as string] as string[]
            )
          )
    return (
      <SelectInput
        key={key}
        source={key}
        // label={'resources.accounts.fields.area'}
        disabled={
          // (index === 0 && !isNil(disabled) && (disabled as boolean)) ||
          (!isNil(disabled) && (disabled as boolean)) ||
          (index > 0 && isNil(selected[organizations[index - 1]]))
        }
        choices={choices}
        onChange={
          index === 0
            ? (
                event:
                  | React.ChangeEvent<HTMLInputElement>
                  | RaRecord<Identifier>
              ): void => {
                // setArea(event.target.value)
                const selected = {
                  [organizations[0]]: event.target.value
                }
                setSelected(selected)
                setValue(organizations[index], event.target.value, {
                  shouldDirty: true,
                  shouldValidate: true
                })
                if (isEmpty(event.target.value)) {
                  setValue(organizations[1], '', {
                    shouldDirty: true
                  })
                }
              }
            : (): void => {}
        }
      />
    )
  })(organizations)
}
